import { useEffect, useMemo, useState } from 'react';
import { get, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { usePlan } from '../lib/plan';
import { formatCents, refundPortion } from '../../shared/money';
import { formatDateTime, formatPhone } from '../../shared/format';
import { REFUND_METHOD_LABELS, refundableByMethod, splitRefund, type RefundPart } from '../../shared/refunds';
import { Badge, Button, Cost, Empty, Field, Money, Profit, Sheet, Spinner, Tabs, statTone, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { useCloudPrint } from '../lib/printing';
import { ReceiptActions, ReceiptPaper, useReceipt } from './Receipt';
import { SaleOrderPrints } from './Orders';
import { SaleRepairPrints } from './Repairs';

type Range = 'today' | 'yesterday' | 'week' | 'month' | 'all';

function rangeBounds(r: Range): { from?: number; to?: number } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (r === 'today') return { from: start.getTime() };
  if (r === 'yesterday') return { from: start.getTime() - 86_400_000, to: start.getTime() };
  if (r === 'week') {
    const day = (start.getDay() + 6) % 7;
    return { from: start.getTime() - day * 86_400_000 };
  }
  if (r === 'month') return { from: new Date(now.getFullYear(), now.getMonth(), 1).getTime() };
  return {};
}

const STATUS: Record<string, { tone: 'green' | 'amber' | 'red'; label: string }> = {
  completed: { tone: 'green', label: 'Paid' },
  partially_refunded: { tone: 'amber', label: 'Part refunded' },
  refunded: { tone: 'red', label: 'Refunded' },
};

export function SalesScreen() {
  const { me, can } = useSession();
  // Cost and profit on each sale: for whoever sees profit (the server sends nobody else the numbers).
  const money = can('profit.view');
  const [range, setRange] = useState<Range>('today');
  const [loc, setLoc] = useState<string>(me?.location?.id ?? '');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const bounds = useMemo(() => rangeBounds(range), [range]);
  const sales = useLoad(() => get(`/api/sales${qs({ ...(dq ? {} : bounds), q: dq, location: loc || 'all', limit: 200 })}`), [range, loc, dq]);
  const total = (sales.data ?? []).reduce((s: number, x: any) => s + x.total_cents - x.refunded_cents, 0);
  return (
    <div className="page">
      <div className="page-head">
        <h1>Sales</h1>
        {me?.register ? <Button variant="primary" size="lg" icon="bag" onClick={() => navigate('/sale')}>New sale</Button> : null}
      </div>
      <div className="row-wrap">
        <Tabs label="Date range" value={range} onChange={setRange} tabs={[{ id: 'today', label: 'Today' }, { id: 'yesterday', label: 'Yesterday' }, { id: 'week', label: 'This week' }, { id: 'month', label: 'This month' }, { id: 'all', label: 'All' }]} />
        {(me?.locations.length ?? 0) > 1 ? (
          <select className="select" style={{ width: 'auto' }} value={loc} onChange={(e) => setLoc(e.target.value)} aria-label="Store">
            <option value="">All stores</option>
            {me?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        ) : null}
        <div className="search grow" style={{ minWidth: 220 }}>
          <Icon.search />
          <input className="input" placeholder="Receipt #, customer phone or name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search sales" />
        </div>
      </div>
      {sales.loading && !sales.data ? <Spinner /> : null}
      {sales.data ? (
        sales.data.length ? (
          <>
            <p className="muted">{sales.data.length} sale{sales.data.length === 1 ? '' : 's'} · {formatCents(total)} after refunds</p>
            <div className="list">
              <table className="table">
                <thead><tr><th>Receipt</th><th>Time</th><th>Customer</th><th>Items</th><th>Paid by</th><th className="right">Total</th>{money ? <th className="right">Cost</th> : null}{money ? <th className="right">Profit</th> : null}<th>Status</th></tr></thead>
                <tbody>
                  {sales.data.map((s: any) => (
                    <tr key={s.id} className="click" onClick={() => navigate(`/sales/${s.id}`)}>
                      <td className="strong">#{s.receipt_no}</td>
                      <td className="nowrap">{formatDateTime(s.created_at)}{loc ? '' : <div className="tiny muted">{s.location_name}</div>}</td>
                      <td>{s.customer_name || <span className="muted">Walk-in</span>}</td>
                      <td className="small" style={{ maxWidth: 320 }}>{s.items}</td>
                      <td className="small">{(s.methods ?? '').replace('store_credit', 'store credit')}</td>
                      <td className="right strong"><Money cents={s.total_cents} /></td>
                      {money ? <td className="right"><Cost cents={s.cost_cents} label={false} /></td> : null}
                      {money ? <td className="right"><Profit cents={s.profit_cents} label={(s.profit_cents ?? 0) < 0 ? 'Loss' : false} /></td> : null}
                      <td><Badge tone={STATUS[s.status]?.tone ?? 'green'}>{STATUS[s.status]?.label ?? s.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <Empty icon="bag" title="No sales here yet." />
        )
      ) : null}
    </div>
  );
}

/** Sales whose "Sale complete" screen already printed its receipt or opened the drawer (this tab), so a reload or a second visit doesn't do it again. */
const doneSales = new Set<string>();
function firstTimeDone(saleId: string): boolean {
  if (doneSales.has(saleId)) return false;
  doneSales.add(saleId);
  try {
    const key = 'sos_sale_done';
    const list: string[] = JSON.parse(sessionStorage.getItem(key) || '[]');
    if (list.includes(saleId)) return false;
    sessionStorage.setItem(key, JSON.stringify([...list.slice(-40), saleId]));
  } catch {
    // No session storage (private browsing): the set above still stops a second print on this page.
  }
  return true;
}

export function SaleDetailScreen({ id, justSold }: { id: string; justSold: boolean }) {
  const { me, can } = useSession();
  const sale = useLoad(() => get(`/api/sales/${id}`), [id]);
  const receipt = useReceipt(id);
  const [refunding, setRefunding] = useState(false);
  const cloud = useCloudPrint();
  const print = usePrint();
  const s = sale.data;
  const r = receipt.data;
  // Right after the sale, once: the receipt prints by itself when that's on (Settings → Printers),
  // and a cash sale opens the drawer (with the receipt when it prints through PrintNode).
  useEffect(() => {
    if (!justSold || !s || !r || s.id !== id || !firstTimeDone(id)) return;
    const cash = s.payments.some((p: any) => p.method === 'cash' && p.amount_cents > 0);
    (async () => {
      // The setting as it is now (the owner may have just turned it on from another device).
      const st = await cloud.refreshStatus();
      const autoPrint = st?.auto_print ?? me?.settings?.auto_print_receipt === '1';
      if (autoPrint) {
        await cloud.printOut({ kind: 'sale_receipt', refId: id, title: `Receipt #${r.receipt_no}`, requestKey: `sale-done-${id}`, openDrawer: cash, browser: () => print(<ReceiptPaper r={r} />, 'thermal') });
      } else if (cash) {
        await cloud.openDrawer('sale', id);
      }
    })().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justSold, id, s, r]);
  if (!s || !receipt.data) return <Spinner />;
  const change = s.payments.reduce((sum: number, p: any) => sum + (p.change_cents ?? 0), 0);
  const refundable = s.lines.some((l: any) => l.qty > l.refunded_qty);
  return (
    <div className="page">
      {justSold ? (
        <div className="card stack" style={{ textAlign: 'center' }}>
          <div className="done-mark"><Icon.check /></div>
          <h1>Sale complete</h1>
          {change ? <p style={{ fontSize: 28, lineHeight: '36px', fontWeight: 700 }}>Change due: {formatCents(change)}</p> : <p className="muted">Receipt #{s.receipt_no} · {formatCents(s.total_cents)}</p>}
          <div className="row-wrap" style={{ justifyContent: 'center' }}>
            <ReceiptActions saleId={id} r={receipt.data} customerPhone={s.customer?.phone} customerEmail={s.customer?.email} />
            <Button variant="primary" size="lg" icon="bag" onClick={() => navigate('/sale')}>New sale</Button>
          </div>
          <SaleOrderPrints lines={s.lines} />
          <SaleRepairPrints lines={s.lines} />
        </div>
      ) : (
        <div className="page-head">
          <div style={{ marginRight: 'auto' }}>
            <h1>Receipt #{s.receipt_no}</h1>
            <p className="muted">{formatDateTime(s.created_at)} · {s.location_name} · {s.employee_name}{s.approved_by_name ? ` (approved by ${s.approved_by_name})` : ''}</p>
          </div>
          <ReceiptActions saleId={id} r={receipt.data} customerPhone={s.customer?.phone} customerEmail={s.customer?.email} />
          {refundable && me?.register ? <Button icon="refresh" onClick={() => setRefunding(true)}>Refund</Button> : null}
        </div>
      )}
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <div><ReceiptPaper r={receipt.data} /></div>
        <div className="stack">
          {s.customer ? (
            <a className="card card-tight row" href={`#/customers/${s.customer.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="avatar avatar-sm">{s.customer.name.slice(0, 1)}</span>
              <span className="grow"><span className="strong" style={{ display: 'block' }}>{s.customer.name}</span><span className="small muted">{formatPhone(s.customer.phone)}</span></span>
              <Icon.chevronRight />
            </a>
          ) : null}
          {s.tax_removed_cents > 0 ? (
            <div className="banner">
              <Icon.tag />
              <div className="small"><span className="strong">Taxes removed: {formatCents(s.tax_removed_cents)}</span> · {s.tax_removed_reason}{s.approved_by_name ? ` · approved by ${s.approved_by_name}` : ''}</div>
            </div>
          ) : null}
          {/* Cost and profit never show on the checkout screen (the customer can see it); they're in the sale's details from Sales history. */}
          {can('profit.view') && s.profit_cents !== null && !justSold ? (
            <>
              <div className="stat-row">
                <div className="stat"><div className="k">Sales (before tax)</div><div className="v">{formatCents(s.total_cents - s.tax_cents)}</div></div>
                <div className="stat stat-cost"><div className="k">Cost</div><div className="v">{formatCents(s.cost_cents)}</div></div>
                <div className={`stat ${statTone('profit', s.profit_cents)}`}><div className="k">{s.profit_cents < 0 ? 'Gross loss' : 'Gross profit'}</div><div className="v">{formatCents(Math.abs(s.profit_cents))}</div></div>
              </div>
              <div className="card card-tight stack-sm" aria-label="Cost and profit by item">
                <h3>By item</h3>
                {s.lines.map((l: any) => (
                  <div key={l.id} className="spread small" style={{ gap: 12 }}>
                    <span>{l.qty !== 1 ? `${l.qty} × ` : ''}{l.description}{l.detail ? <span className="muted"> · {l.detail}</span> : null}</span>
                    <span className="pill-row" style={{ justifyContent: 'flex-end' }}>
                      <span className="num strong">{formatCents(l.net_cents)}</span>
                      {l.unit_cost_cents !== null && l.unit_cost_cents !== undefined ? <Cost cents={l.unit_cost_cents * l.qty} /> : null}
                      {l.profit_cents !== null && l.profit_cents !== undefined ? <Profit cents={l.profit_cents} /> : null}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          {s.refunds.length ? (
            <div className="card card-tight stack-sm">
              <h3>Refunds</h3>
              {s.refunds.map((r: any) => (
                <div key={r.id} className="spread small">
                  <span>#{r.refund_no} · {formatDateTime(r.created_at)} · {r.employee_name}{r.approved_by_name ? ` (approved by ${r.approved_by_name})` : ''} · {r.reason} · {refundWays(r)}</span>
                  <span className="strong num">−{formatCents(r.total_cents)}</span>
                </div>
              ))}
            </div>
          ) : null}
          {!justSold ? null : (
            <Button variant="ghost" onClick={() => navigate(`/sales/${id}`)}>Show sale details</Button>
          )}
        </div>
      </div>
      {refunding ? <RefundSheet sale={s} onClose={() => setRefunding(false)} onDone={() => { setRefunding(false); sale.reload(); receipt.reload(); }} /> : null}
    </div>
  );
}

/** How a refund's money went back: "cash", or "card $300.00 + store credit $200.00". */
function refundWays(r: any): string {
  const parts: RefundPart[] = r.parts?.length ? r.parts : [{ method: r.method, amount_cents: r.total_cents }];
  const word = (m: string) => ((REFUND_METHOD_LABELS as Record<string, string>)[m] ?? m.replace('_', ' ')).toLowerCase();
  if (parts.length === 1) return word(parts[0].method);
  return parts.map((p) => `${word(p.method)} ${formatCents(p.amount_cents)}`).join(' + ');
}

type RefundChoice = 'original' | 'cash' | 'card' | 'store_credit';

function RefundSheet({ sale, onClose, onDone }: { sale: any; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const toast = useToast();
  // How the sale was paid: one way, the refund goes back that way; several (a card and trade-in
  // credit, cash and a card), it goes back the same ways, each its own part.
  const paidWays = [...new Set<string>(sale.payments.filter((p: any) => p.amount_cents > 0).map((p: any) => p.method))];
  const split = paidWays.length > 1;
  const first: RefundChoice = split ? 'original' : paidWays[0] === 'card' || paidWays[0] === 'store_credit' ? paidWays[0] : 'cash';
  const [qty, setQty] = useState<Record<string, number>>({});
  const [restock, setRestock] = useState<Record<string, boolean>>({});
  const [method, setMethod] = useState<RefundChoice>(first);
  const [reason, setReason] = useState('');
  const [ref, setRef] = useState('');
  const open = sale.lines.filter((l: any) => l.qty > l.refunded_qty);
  const chosen = sale.lines.filter((l: any) => (qty[l.id] ?? 0) > 0);
  const all = open.length > 0 && open.every((l: any) => qty[l.id] === l.qty - l.refunded_qty);
  // Exactly what the server gives back: the same share of each line (its tax included), the last ones get what's left.
  const total = chosen.reduce((s: number, l: any) => {
    const p = refundPortion({ qty: l.qty, net: l.net_cents, tax: l.tax_cents, refundedQty: l.refunded_qty, refundedNet: l.refunded_net_cents ?? 0, refundedTax: l.refunded_tax_cents ?? 0 }, qty[l.id]);
    return s + p.net + p.tax;
  }, 0);
  const done = sale.refunds.flatMap((r: any) => (r.parts?.length ? r.parts : [{ method: r.method, amount_cents: r.total_cents }]));
  const parts: RefundPart[] = method === 'original' ? splitRefund(total, refundableByMethod(sale.payments, done)) : total > 0 ? [{ method, amount_cents: total }] : [];
  const cardPart = parts.some((p) => p.method === 'card');
  const cardBack = parts.filter((p) => p.method === 'card').reduce((s, p) => s + p.amount_cents, 0);
  // The card payment STORE OS took on the Clover Mini (if that's how they paid): the refund goes
  // back to the card through the Mini, then it's recorded here.
  const cloverPay = sale.payments.find((p: any) => p.method === 'card' && /^clover:[A-Za-z0-9]+$/.test(p.external_ref ?? ''));
  const cloverId: string | null = cloverPay ? String(cloverPay.external_ref).slice('clover:'.length) : null;
  const { me } = useSession();
  const own = usePlan().home;
  const viaClover = !!cloverId && !!me?.clover?.ready && cardPart && cardBack > 0;
  // The Mini already gave the money back, but recording it here didn't finish (a wrong PIN, no
  // connection): the reference is kept so it can be recorded without refunding the card twice.
  const [cloverDone, setCloverDone] = useState<{ ref: string; cents: number } | null>(null);
  const [onClover, setOnClover] = useState(false);
  const everything = () => setQty(Object.fromEntries(open.map((l: any) => [l.id, l.qty - l.refunded_qty])));
  const submit = async () => {
    let cardRef = cardPart ? ref || undefined : undefined;
    if (viaClover && !cloverDone) {
      setOnClover(true);
      try {
        const c = await post('/api/clover/refund', { saleId: sale.id, cloverPaymentId: cloverId, amountCents: cardBack });
        setCloverDone({ ref: c.external_ref, cents: c.amount_cents });
        cardRef = c.external_ref;
      } catch (e) {
        toast(e instanceof Error ? e.message : 'The Clover didn’t refund the card.', 'error');
        return;
      } finally {
        setOnClover(false);
      }
    } else if (cloverDone) {
      cardRef = cloverDone.ref;
    }
    const r = await run((approval?: ApprovalInput) =>
      post(`/api/sales/${sale.id}/refund`, {
        lines: chosen.map((l: any) => ({ saleLineId: l.id, qty: qty[l.id], restock: restock[l.id] !== false })),
        method,
        reason,
        externalRef: cardRef,
        approval,
      }),
    );
    if (!r) return;
    const back: RefundPart[] = r.parts ?? [];
    toast(back.length > 1 ? `Refunded ${formatCents(r.total_cents)}: ${refundWays(r)}.` : `Refunded ${formatCents(r.total_cents)}${back.length ? ` (${refundWays(r)})` : ''}.`, 'good');
    onDone();
  };
  const choices: [RefundChoice, string][] = [
    ...(split ? [['original', 'Same way they paid'] as [RefundChoice, string]] : []),
    ['cash', 'Cash from drawer'], ['card', own ? 'Card (refund on the Clover)' : 'Card (refund on your card reader)'], ['store_credit', 'Store credit'],
  ];
  return (
    <Sheet
      title={`Refund receipt #${sale.receipt_no}`}
      onClose={onClose}
      wide
      footer={<><Button onClick={onClose} disabled={!!cloverDone}>Cancel</Button><Button variant="primary" busy={busy || onClover} disabled={!chosen.length || !reason.trim()} onClick={submit}>{viaClover && !cloverDone ? `Refund ${formatCents(total)} (card on the Clover)` : `Refund ${formatCents(total)}`}</Button></>}
    >
      {cloverDone ? <div className="banner warn"><Icon.alert /><div className="small"><strong>{formatCents(cloverDone.cents)} already went back to the card on the Clover</strong> ({cloverDone.ref}). Finish recording the refund now; the card won’t be charged or refunded again.</div></div> : null}
      {onClover ? <div className="banner"><Icon.info /><div className="small">Refunding {formatCents(cardBack)} to the card on the Clover Mini…</div></div> : null}
      {open.length ? (
        <div className="row-wrap">
          <Button icon="refresh" onClick={everything} disabled={all}>Refund everything</Button>
          {chosen.length ? <Button variant="ghost" onClick={() => setQty({})}>Clear</Button> : null}
          <span className="small muted">{all ? 'Everything left on the receipt is picked.' : 'Or pick the items below.'}</span>
        </div>
      ) : null}
      <div className="list">
        {sale.lines.map((l: any) => {
          const left = l.qty - l.refunded_qty;
          const n = qty[l.id] ?? 0;
          const stock = l.kind === 'product' || l.kind === 'device';
          return (
            <div key={l.id} className="list-row" style={{ cursor: 'default', flexWrap: 'wrap' }}>
              <span className="main-col"><span className="t" style={{ display: 'block' }}>{l.description}</span><span className="s" style={{ display: 'block' }}>{left ? `${left} of ${l.qty} can be refunded` : 'Already refunded'}</span></span>
              {left ? (
                <>
                  <div className="qty">
                    <button type="button" aria-label="Less" onClick={() => setQty({ ...qty, [l.id]: Math.max(0, n - 1) })}><Icon.minus /></button>
                    <span>{n}</span>
                    <button type="button" aria-label="More" onClick={() => setQty({ ...qty, [l.id]: Math.min(left, n + 1) })}><Icon.plus /></button>
                  </div>
                  {stock ? <label className="check small"><input type="checkbox" checked={restock[l.id] !== false} onChange={(e) => setRestock({ ...restock, [l.id]: e.target.checked })} />{l.kind === 'device' ? 'Back to inventory (needs testing)' : 'Put back in stock'}</label> : null}
                </>
              ) : null}
            </div>
          );
        })}
      </div>
      {/* Not a <label>: each choice keeps its own name for screen readers. */}
      <div className="field">
        <span id="refund-to">Refund to</span>
        <div className="chips" role="group" aria-labelledby="refund-to">
          {choices.map(([v, label]) => (
            <button key={v} type="button" className="chip" aria-pressed={method === v} disabled={v === 'store_credit' && !sale.customer} onClick={() => setMethod(v)}>{label}</button>
          ))}
        </div>
      </div>
      {method === 'original' && parts.length ? (
        <div className="banner">
          <Icon.info />
          <div className="small grow">
            {parts.map((p) => <div key={p.method} className="spread"><span>{p.method === 'cash' ? 'Cash from the drawer' : p.method === 'card' ? (own ? 'Back to the card (on the Clover)' : 'Back to the card') : 'Store credit, back on their account'}</span><span className="strong num">{formatCents(p.amount_cents)}</span></div>)}
          </div>
        </div>
      ) : null}
      {cardPart && !viaClover ? <Field label={own ? 'Clover refund reference (optional)' : 'Card refund reference (optional)'}><input className="input" value={ref} onChange={(e) => setRef(e.target.value)} /></Field> : null}
      {viaClover && !cloverDone ? <p className="small muted">{formatCents(cardBack)} goes back to the card {cloverPay.card_last4 ? `••••${cloverPay.card_last4} ` : ''}through the Clover Mini, then the refund is recorded.</p> : null}
      <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Wrong size case, returned within 14 days" /></Field>
      <p className="small muted">Refunds are logged with your name. A manager PIN may be needed.</p>
    </Sheet>
  );
}
